/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { ProgramType } from 'client-code/airing';
import PropTypes from 'prop-types';

import { DESCRIPTION_MAX_LENGTH } from '../../../../config';
import { AiringPreviewUtils } from '../../../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../../../core/videoplayer/VideoPlaybackController';
import { EventDescriptionProps } from './EventDescriptionProps';

import './EventDescription.scss';

const COMMA = <span>,</span>;
const DASH = <span>&#8211;</span>;
const EMPTY_SPACE = <span>&nbsp;</span>;
const LEFT_QUOTE = <span>&#8216;</span>;
const RIGHT_QUOTE = <span>&#8217;</span>;

const EventDescription: FunctionComponent<EventDescriptionProps> = (props): JSX.Element => {
    const { mAiringData, mPreviewData } = props;

    const dataFormatter = new AiringPreviewUtils(mAiringData, mPreviewData);
    const cast = dataFormatter.getCast();
    const description = dataFormatter.getDescription();
    const episodeTitle = dataFormatter.getEpisodeTitle();
    const firstAiredDate = dataFormatter.getFirstAireddate();
    const genre = dataFormatter.generateGenre();
    const programType = dataFormatter.getProgramType();
    const showAttributes = dataFormatter.getShowAttributes(
        VideoPlaybackController.getInstance().getPlayerStats().height,
    );

    const episodeTitleLength = episodeTitle.length ? episodeTitle.length + 3 : 0;
    const descriptionLength = episodeTitleLength + description.length;

    const hasTruncation = descriptionLength >= DESCRIPTION_MAX_LENGTH;

    const genreCastSeperator = (genre || firstAiredDate) && cast;
    const renderEpisodeTitle = () =>
        episodeTitle && (
            <span data-testid="event-description-episode-title">
                {LEFT_QUOTE}
                {episodeTitle}
                {RIGHT_QUOTE}
                {DASH}
                {EMPTY_SPACE}
            </span>
        );

    const renderShowAttributes = () =>
        showAttributes && (
            <div
                className={`show-attributes-container ${hasTruncation ? 'truncate' : ''}`}
                data-testid="event-description-show-attributes"
            >
                {hasTruncation && <div className="transparent-effect"></div>}
                <div className={hasTruncation ? 'show-attributes-truncate' : ''}>
                    ({showAttributes})
                </div>
            </div>
        );

    const renderGenre = () => (
        <div className="genre" data-testid="event-description-genre">
            {genre}
            {genre && firstAiredDate && programType !== ProgramType.Movie && (
                <>
                    {COMMA} {EMPTY_SPACE}
                </>
            )}
            {programType !== ProgramType.Movie && firstAiredDate}
        </div>
    );

    const renderGenreCastSeparator = () =>
        genreCastSeperator && (
            <div
                className="genre-cast-seperator"
                data-testid="event-description-genre-cast-separator"
            />
        );

    const renderCast = () =>
        cast && (
            <div className="cast" data-testid="event-description-casst">
                {cast}
            </div>
        );

    return (
        <div className="event-description-container">
            <div className="description" data-testid="event-description">
                {programType === ProgramType.Episode && renderEpisodeTitle()}
                {description}
                {EMPTY_SPACE}
                {renderShowAttributes()}
            </div>
            <div className="metadata-container" data-testid="event-description-metadata-container">
                {renderGenre()}
                {renderGenreCastSeparator()}
                {renderCast()}
            </div>
        </div>
    );
};

EventDescription.propTypes = {
    mAiringData: PropTypes.any.isRequired,
    mPreviewData: PropTypes.any.isRequired,
};

export default EventDescription;
